/* Verona Tempo round 10 — see REVAMP-LOG.md */
/**
 * Round 10 (A) — cards, wishlist and bag extras. Deferred on every page after vt-theme.js / vt-cart.js (uses window.vtCore).
 *  - "Move to wishlist" on bag lines (drawer + bag page; snippets/cart-item.liquid): saves the product in the wishlist
 *    (vtCore.wishlist) and removes the line with the bag's own Remove link, so Undo, the order summary, the polite
 *    messages and the focus rescue (vt-cart.js) all work exactly as for Remove.
 *  - Wishlist page "Add all to bag" (sections/vt-wishlist.liquid): every wishlist product that is in stock and needs no
 *    choice is added in one /cart/add.js call (one variant in stock, or only the size is open and her size is remembered —
 *    "U1 remember her size"). Products already in the bag and sold-out products are skipped (never added). Products that
 *    need a size are listed with "Choose size" buttons that open the quick-add panel (or the product page).
 *    The bag drawer is refreshed the same way the theme does after an add (vtCore.openCartAfterAdd / section HTML).
 * Texts: the wishlist section's [data-vt-wl-strings] JSON and data-attributes on the bag buttons (no English in JS).
 * Everything else about the round-10 cards is Liquid + CSS (snippets/product-block.liquid, assets/vt-r10-cards.css).
 */
(function () {
  'use strict';

  var core = window.vtCore;
  if (!core) return;
  var tpl = core.tpl;
  var esc = core.esc;
  var route = core.route;

  /* ------------------------------------------------------------ helpers */
  function sectionIds() {
    return ['page-header', 'cart-drawer'].map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el && el.dataset.sectionId; })
      .map(function (el) { return el.dataset.sectionId; });
  }
  function updateSections(sections) {
    if (!sections) return;
    ['page-header', 'cart-drawer'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el && el.dataset.sectionId && sections[el.dataset.sectionId] && el.updateFromCartChange) {
        el.updateFromCartChange(sections[el.dataset.sectionId]);
      }
    });
  }
  function optionName(o) { return typeof o === 'string' ? o : (o && o.name) || ''; }
  // same grouping as the quick-add panel's size memory (shoes / bags / product type)
  function sizeCategory(p) {
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('shoes') > -1) return 'shoes';
    if (tags.indexOf('bags') > -1) return 'bags';
    return String(p.type || '').toLowerCase().replace(/[^a-z0-9؀-ۿ]+/g, '-').replace(/^-|-$/g, '');
  }
  function uniq(list) { return list.filter(function (v, i) { return list.indexOf(v) === i; }); }

  /** { variant } to add, { sold: true } or { need: true } (she has to choose) */
  function pickVariant(p, memory) {
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    if (!p.available || !avail.length) return { sold: true };
    if (avail.length === 1) return { variant: avail[0] };
    var opts = p.options || [];
    var si = -1;
    opts.forEach(function (o, i) { if (si === -1 && core.isSizeOption(optionName(o))) si = i; });
    if (si > -1) {
      var fixed = opts.every(function (o, i) {
        return i === si || uniq(avail.map(function (v) { return (v.options || [])[i]; })).length === 1;
      });
      var mem = memory[sizeCategory(p)];
      if (fixed && mem) {
        var match = avail.filter(function (v) { return String((v.options || [])[si]) === String(mem); })[0];
        if (match) return { variant: match };
      }
    }
    return { need: true };
  }

  function postAdd(items, withSections) {
    var body = { items: items };
    if (withSections) { body.sections = sectionIds().join(','); body.sections_url = window.location.pathname; }
    return fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok && !d.status, data: d }; }, function () { return { ok: r.ok, data: {} }; });
    }).catch(function () { return { ok: false, data: {} }; });
  }

  /* ------------------------------------------------------------ wishlist: add all to bag */
  function initAddAll() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vt-wl-addall]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      evt.preventDefault();
      addAll(btn);
    });
  }

  function addAll(btn) {
    var grid = btn.closest('vt-wishlist-grid');
    var W = core.parseJsonEl(grid && grid.querySelector('[data-vt-wl-strings]'), {});
    var box = btn.closest('[data-vt-wl-addall-box]');
    var result = box && box.querySelector('[data-vt-wl-result]');
    var textEl = btn.querySelector('[data-vt-wl-addall-text]') || btn;
    var handles = core.wishlist.get().slice(0, 48);
    if (!handles.length) return;
    btn.setAttribute('aria-busy', 'true'); // (not "disabled": keyboard focus stays on the button)
    textEl.textContent = W.adding || textEl.textContent;
    if (result) result.innerHTML = '';
    var memory = core.readSizeMemory ? core.readSizeMemory() : {};

    var cartReq = fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; });

    Promise.all([cartReq, Promise.all(handles.map(core.fetchProduct))]).then(function (res) {
      var inCart = {};
      (res[0].items || []).forEach(function (it) { inCart[it.product_id] = true; });
      var toAdd = [];
      var need = [];
      var sold = 0;
      var already = 0;
      res[1].forEach(function (p) {
        if (!p) return;
        if (inCart[p.id]) { already += 1; return; }
        var pick = pickVariant(p, memory);
        if (pick.sold) sold += 1;
        else if (pick.need) need.push(p);
        else toAdd.push({ id: pick.variant.id, quantity: 1, title: p.title });
      });
      if (!toAdd.length) return { added: 0, need: need, sold: sold, already: already };
      var items = toAdd.map(function (x) { return { id: x.id, quantity: 1 }; });
      return postAdd(items, true).then(function (r) {
        if (r.ok) return { added: items.length, need: need, sold: sold, already: already, sections: r.data && r.data.sections };
        // one item could not be added (e.g. just sold out): add them one by one, keep the ones that work
        var added = 0;
        var chain = Promise.resolve();
        items.forEach(function (it) {
          chain = chain.then(function () { return postAdd([it], false).then(function (x) { if (x.ok) added += 1; else sold += 1; }); });
        });
        return chain.then(function () { return { added: added, need: need, sold: sold, already: already, sections: null, partial: true }; });
      });
    }).then(function (out) {
      finish(btn, textEl, W, result, out);
    }).catch(function () {
      finish(btn, textEl, W, result, null);
    });
  }

  function finish(btn, textEl, W, result, out) {
    btn.removeAttribute('aria-busy');
    textEl.textContent = W.addAll || textEl.textContent;
    if (!out) {
      if (result) result.innerHTML = '<p class="vt-wl10__msg is-error">' + esc(W.error || '') + '</p>';
      core.announce(W.error);
      return;
    }
    var parts = [];
    if (out.added === 1) parts.push(W.addedOne);
    else if (out.added > 1) parts.push(tpl(W.addedOther, { count: out.added }));
    else parts.push(W.noneAdded);
    if (out.already) parts.push(tpl(W.inBag, { count: out.already }));
    if (out.sold) parts.push(tpl(W.soldOut, { count: out.sold }));
    var summary = parts.filter(Boolean).join(' ');

    var html = '<p class="vt-wl10__msg">' + esc(summary) + '</p>';
    if (out.need.length) {
      var titles = out.need.map(function (p) { return p.title; }).join(', ');
      var qa = !!(window.vtConfig && window.vtConfig.quickAdd);
      html += '<p class="vt-wl10__msg vt-wl10__msg--need">' + esc(tpl(W.chooseFor, { titles: titles })) + '</p><div class="vt-wl10__choose">' +
        out.need.map(function (p) {
          var label = esc(tpl(W.chooseBtn, { title: p.title }));
          return qa
            ? '<button type="button" class="btn btn--secondary vt-wl10__choose-btn" data-vt-qa="' + esc(p.handle) + '">' + label + '</button>'
            : '<a class="btn btn--secondary vt-wl10__choose-btn" href="' + esc(route('products/' + p.handle)) + '">' + label + '</a>';
        }).join('') + '</div>';
    }
    if (out.added && out.need.length) {
      html += '<button type="button" class="vt-wl10__viewbag" data-vt-wl-viewbag>' + esc(W.viewBag || '') + '</button>';
    }
    if (result) result.innerHTML = html;
    core.announce(summary + (out.need.length ? ' ' + tpl(W.chooseFor, { titles: out.need.map(function (p) { return p.title; }).join(', ') }) : ''));

    if (out.added) {
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { vtAddAll: true } }));
      if (!out.need.length) {
        // everything that could be added is in: open the bag like any other add
        core.openCartAfterAdd(btn, out.sections || null);
      } else {
        // she still has sizes to choose here: refresh the bag + header count without covering the page
        if (out.sections) updateSections(out.sections);
        else document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        var first = result && result.querySelector('.vt-wl10__choose-btn');
        if (first) first.focus();
      }
    } else {
      var firstChoose = result && result.querySelector('.vt-wl10__choose-btn');
      if (firstChoose) firstChoose.focus();
    }
  }

  function initViewBag() {
    document.addEventListener('click', function (evt) {
      var b = evt.target.closest && evt.target.closest('[data-vt-wl-viewbag]');
      if (!b) return;
      evt.preventDefault();
      if (document.querySelector('cart-drawer.js-cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: b } }));
      } else {
        window.location.href = route('cart');
      }
    });
  }

  /* ------------------------------------------------------------ bag: move to wishlist */
  function initMoveToWishlist() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vt-move-wish]');
      if (!btn) return;
      evt.preventDefault();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      var item = btn.closest('.cart-item');
      var remove = item && item.querySelector('.cart-item__remove');
      var h = btn.dataset.handle;
      if (!remove || !h || !core.wishlist) return;
      if (!core.wishlist.has(h)) core.wishlist.toggle(h); // adds to the top of her list (fires vt:wishlist:change)
      // not "disabled": that would drop keyboard focus before the line has left
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      // the bag's own Remove: undo message, order summary, polite messages and focus rescue stay the same
      var drawer = item.closest('cart-drawer');
      remove.click();
      core.announce(btn.dataset.done);
      // focus never gets lost when the line leaves: the "Undo" button, else the next line, else the drawer / page title
      var tryFocus = function (el) {
        if (!el) return false;
        el.focus();
        return document.activeElement === el;
      };
      var rescue = function () {
        var a = document.activeElement;
        if (a && a !== document.body && a.isConnected) return;
        if (tryFocus(document.querySelector('.vt-undo .vt-undo__btn:not([disabled]):not([hidden])'))) return;
        var scope = drawer || document.querySelector('cart-form');
        if (tryFocus(scope && scope.querySelector('.cart-item .cart-item__title a'))) return;
        if (drawer && drawer.hasAttribute('open') && tryFocus(drawer)) return;
        var h = document.querySelector('main h1, #content h1');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
      };
      // after the bag is redrawn (removed lines fade out first, then leave the page) + a late safety net
      var onMerge = function () {
        document.removeEventListener('on:cart:after-merge', onMerge);
        window.setTimeout(rescue, 80);
        window.setTimeout(rescue, 1100);
      };
      document.addEventListener('on:cart:after-merge', onMerge);
      window.setTimeout(function () { document.removeEventListener('on:cart:after-merge', onMerge); rescue(); }, 6000);
    });
  }

  /* ------------------------------------------------------------ r11 (U4): swipe photos on phone cards
     Cards with data-vt11-swipe (snippets/product-block.liquid, setting "Swipe photos on phone product cards"), phones
     only (≤767px, no hover = touch): a horizontal scroll-snap track with the first 3 product photos and small dots.
     The track is built only when the card comes near the screen (IntersectionObserver) from the card's own photos:
     the first photo is copied (already downloaded), photos 2–3 are the card's hidden extra photos (not downloaded
     until then, loading="lazy"). The track sits inside the card link, so a tap still opens the product; vertical page
     scrolling is not blocked (horizontal-only scroller). Symmetry's own touch swipe / dots / arrows are hidden on
     phones for these cards (assets/vt-r10-cards.css). Not in carousels (the row itself scrolls sideways). */
  var SW_MQ = window.matchMedia ? window.matchMedia('(max-width: 767.98px) and (hover: none)') : null;
  var swObserver = null;
  function swDots(track, dots) {
    var w = track.clientWidth || 1;
    var i = Math.round(Math.abs(track.scrollLeft) / w);
    Array.prototype.forEach.call(dots.children, function (d, n) { d.classList.toggle('is-on', n === i); });
  }
  function swSlide(from, eager) {
    var slide = document.createElement('div');
    slide.className = 'vt11-sw__slide';
    var inner = from && from.firstElementChild ? from.firstElementChild.cloneNode(true) : null;
    if (!inner) return null;
    inner.querySelectorAll('img').forEach(function (img) {
      // 27 Oct 2026: the copy must not keep the original's 'still loading = invisible' mark (assets/vt-r12-look.js)
      img.classList.remove('vt-r12-ld', 'vt-r12-in');
      img.removeAttribute('fetchpriority');
      img.setAttribute('loading', eager ? 'eager' : 'lazy');
    });
    slide.appendChild(inner);
    return slide;
  }
  function buildSwipe(block) {
    if (block.vt11Built) return;
    block.vt11Built = true;
    var host = block.querySelector('.image-cont .image-label-wrap > div');
    var primary = host && host.querySelector('.product-block__image--primary');
    if (!primary) return;
    var extras = [1, 2].map(function (n) { return host.querySelector('.product-block__image--secondary[data-image-index="' + n + '"]'); }).filter(Boolean);
    if (!extras.length) return;
    var track = document.createElement('div');
    track.className = 'vt11-sw__track';
    var dots = document.createElement('div');
    dots.className = 'vt11-sw__dots';
    dots.setAttribute('aria-hidden', 'true');
    [primary].concat(extras).forEach(function (el, n) {
      var s = swSlide(el, n === 0);
      if (!s) return;
      track.appendChild(s);
      dots.appendChild(document.createElement('span'));
    });
    if (track.children.length < 2) return;
    host.classList.add('vt11-sw-host');
    // 2 Nov 2026 (owner: "items glitching in and out"): the strip covered the card's photo with an empty box until its
    // own copy of the photo was drawn (and lined up), so the photo blinked. It now stays unseen, with the card's photo
    // showing through, until that copy is ready (at most ~1 s)
    track.classList.add('vt11-sw__track--wait');
    host.appendChild(track);
    host.appendChild(dots);
    block.classList.add('vt11-sw');
    swDots(track, dots);
    var firstImg = track.querySelector('img');
    var lineOn = document.documentElement.classList.contains('vt-line');
    var swReady = function () { return !firstImg || (firstImg.complete && (!lineOn || firstImg.classList.contains('is-lined') || firstImg.classList.contains('vt-lw-ok') || !firstImg.naturalWidth)); };
    var swShow = function () { track.classList.remove('vt11-sw__track--wait'); };
    if (swReady()) window.requestAnimationFrame(swShow);
    else {
      var swTries = 0;
      var swIv = window.setInterval(function () { if (swReady() || ++swTries > 16) { window.clearInterval(swIv); window.requestAnimationFrame(swShow); } }, 60);
    }
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = 0; swDots(track, dots); });
    }, { passive: true });
    // keep Symmetry's own swipe (it fades the hidden photos) out of it
    ['touchstart', 'touchmove'].forEach(function (t) { track.addEventListener(t, function (e) { e.stopPropagation(); }, { passive: true }); });
    // a colour dot on the card: show that colour's photo first
    block.addEventListener('click', function (evt) {
      var sw = evt.target.closest && evt.target.closest('.product-block-options__item[data-media]');
      if (!sw) return;
      var src = host.querySelector('.product-block__image[data-media-id="' + sw.dataset.media + '"]');
      var first = src && swSlide(src, true);
      if (!first) return;
      // 2 Nov 2026: the old photo stays until the new colour's photo has arrived (it went blank while it downloaded)
      var token = track.vtSwapToken = (track.vtSwapToken || 0) + 1;
      var swap = function () {
        if (track.vtSwapToken !== token) return;
        track.vtSwapToken = token + 0.5;
        track.replaceChild(first, track.firstElementChild);
        track.scrollTo({ left: 0 });
        swDots(track, dots);
      };
      var im = first.querySelector('img');
      if (!im || (im.complete && im.naturalWidth)) { swap(); return; }
      im.addEventListener('load', swap, { once: true });
      im.addEventListener('error', swap, { once: true });
      window.setTimeout(swap, 2000);
    });
  }
  function scanSwipe() {
    if (!SW_MQ || !SW_MQ.matches) return;
    var cards = document.querySelectorAll('product-block[data-vt11-swipe]');
    Array.prototype.forEach.call(cards, function (block) {
      if (block.vt11Seen || block.closest('.carousel, .product-grid--scrollarea, .slider, .mobile-navigation-drawer, .main-search__results')) return;
      block.vt11Seen = true;
      if (swObserver) swObserver.observe(block);
      else buildSwipe(block);
      block.addEventListener('touchstart', function () { buildSwipe(block); }, { passive: true, once: true });
    });
  }
  function initCardSwipe() {
    if (!SW_MQ || !document.querySelector('product-block[data-vt11-swipe]')) return;
    if ('IntersectionObserver' in window) {
      swObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          swObserver.unobserve(e.target);
          buildSwipe(e.target);
        });
      }, { rootMargin: '300px 0px' });
    }
    scanSwipe();
    document.addEventListener('vt:products:appended', scanSwipe);
    if (core.onListingRefresh) core.onListingRefresh(scanSwipe);
    if (SW_MQ.addEventListener) SW_MQ.addEventListener('change', scanSwipe);
  }

  /* ------------------------------------------------------------ 2 Nov 2026: colour dots without a blank tile
     Choosing a colour on a card (hover / tap) showed an empty tile until that colour's photo had downloaded (owner:
     "items glitching in and out"). The photo shown before stays underneath (.vt-keep) until the new one is ready
     (downloaded and lined up), at most 2.5 s; the new one fades in over it. Styles: assets/vt-r10-cards.css. */
  function initKeepPhoto() {
    var lineOn = function () { return document.documentElement.classList.contains('vt-line'); };
    var ready = function (wrap) {
      var img = wrap && wrap.querySelector('img');
      if (!img || getComputedStyle(wrap).display === 'none') return true;
      return img.complete && (!img.naturalWidth || !lineOn() || img.classList.contains('is-lined') || img.classList.contains('vt-lw-ok'));
    };
    var onPick = function (evt) {
      var sw = evt.target && evt.target.closest && evt.target.closest('.product-block-options__item[data-media]');
      var block = sw && sw.closest('product-block');
      var cont = block && block.querySelector('.image-cont--with-secondary-image');
      if (!cont || cont.vtKeepBusy) return;
      var old = cont.querySelector('.product-block__image--active');
      if (!old || !ready(old)) return;
      cont.vtKeepBusy = true;
      window.setTimeout(function () {
        cont.vtKeepBusy = false;
        var now = cont.querySelector('.product-block__image--active');
        var hover = cont.querySelector('.product-block__image--show-on-hover');
        if (!now || now === old || (ready(now) && (!cont.matches(':hover') || ready(hover)))) return;
        cont.querySelectorAll('.vt-keep').forEach(function (el) { el.classList.remove('vt-keep'); });
        old.classList.add('vt-keep');
        cont.classList.add('vt-keeping');
        var token = cont.vtKeepToken = (cont.vtKeepToken || 0) + 1;
        var t0 = Date.now();
        var done = function () {
          if (cont.vtKeepToken !== token) return;
          old.classList.remove('vt-keep');
          cont.classList.remove('vt-keeping');
        };
        var iv = window.setInterval(function () {
          if (cont.vtKeepToken !== token) { window.clearInterval(iv); return; }
          var a = cont.querySelector('.product-block__image--active');
          // the hover photo only counts while the mouse is on the card (computers show it then)
          var hv = cont.matches(':hover') ? cont.querySelector('.product-block__image--show-on-hover') : null;
          if ((ready(a) && ready(hv)) || Date.now() - t0 > 2500) {
            window.clearInterval(iv);
            // the new photo fades in (~0.35 s) over the old one, then the old one goes
            window.setTimeout(done, 400);
          }
        }, 60);
      }, 0);
    };
    document.addEventListener('mouseenter', onPick, true);
    document.addEventListener('click', onPick, true);
  }

  core.onReady(function () {
    initKeepPhoto();
    initAddAll();
    initViewBag();
    initMoveToWishlist();
    initCardSwipe();
  });
})();
